/**
 * ConnectCta
 *
 * Empty-state chip used across the dashboard whenever a card would show
 * synthetic/dummy numbers because no live integration is connected. Points
 * users straight at the Integrations page with a clear one-line ask.
 *
 * Palette: Blue for QuickBooks (financial), Purple for HubSpot (CRM). No
 * red/green. Small footprint - drops into any card in place of a value.
 */
import { Link2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const TONE = {
  quickbooks: { bg: "#EFF6FF", border: "#1B75BA", ink: "#14588A" },
  hubspot:    { bg: "#F4EDFF", border: "#8B5CF6", ink: "#5B21B6" },
  neutral:    { bg: "#F4F6FB", border: "#9BA4B8", ink: "#4B5570" },
};

const LABEL = {
  quickbooks: "Connect QuickBooks",
  hubspot: "Connect HubSpot",
  neutral: "Connect a Source",
};

/**
 * @param {"quickbooks" | "hubspot" | "neutral"} source
 * @param {string} [text]      Optional override for the CTA copy
 * @param {"chip" | "card"} [variant="chip"]  chip=inline pill, card=full block with description
 * @param {string} [description]  Extra body text (card variant only)
 * @param {string} [testId]
 */
export function ConnectCta({
  source = "quickbooks",
  text,
  variant = "chip",
  description,
  testId,
}) {
  const tone = TONE[source] || TONE.neutral;
  const label = text || LABEL[source] || LABEL.neutral;
  const testid = testId || `connect-cta-${source}-${variant}`;

  if (variant === "card") {
    return (
      <Link
        to="/integrations"
        data-testid={testid}
        className="block rounded-xl p-4 transition-shadow hover:shadow-sm"
        style={{ background: tone.bg, border: `1px dashed ${tone.border}` }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: "#FFFFFF", border: `1px solid ${tone.border}` }}
          >
            <Link2 size={16} style={{ color: tone.ink }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold" style={{ color: tone.ink }}>
              {label}
            </p>
            {description && (
              <p className="text-[11px] mt-0.5" style={{ color: "#6B7491" }}>
                {description}
              </p>
            )}
          </div>
          <ArrowUpRight size={14} style={{ color: tone.ink }} />
        </div>
      </Link>
    );
  }

  return (
    <Link
      to="/integrations"
      data-testid={testid}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 transition-colors hover:brightness-95"
      style={{ background: tone.bg, border: `1px dashed ${tone.border}` }}
    >
      <Link2 size={11} style={{ color: tone.ink }} />
      <span className="text-[10px] font-bold" style={{ color: tone.ink }}>
        {label}
      </span>
      <ArrowUpRight size={10} style={{ color: tone.ink }} />
    </Link>
  );
}

export default ConnectCta;
